// Справка ERP «Бетон»: кнопка «?» в правом нижнем углу. Каждая роль видит только свои разделы,
// сценарии и термины. Названия кнопок — точно как на экранах (при правке экрана править и справку).

import { HelpButton as SharedHelp, type HelpContent, type HelpPageInfo, type HelpScenario, type HelpTerm } from "../shared/help";
import { useRoute } from "../shared/router";
import { ROLES, useCtx, type Role } from "./ctx";

type R = Role;
type Page = "schedule" | "week" | "fleet" | "stock" | "lab" | "shift" | "operator" | "driver" | "dashboard" | "plan" | "reports" | "catalogs" | "admin";
type Info = HelpPageInfo;

const PAGES: { page: Page; title: string; icon: string; text: Partial<Record<R, Info>> }[] = [
  { page: "fleet", title: "Машины", icon: "fleet", text: {
    dispatcher: {
      about: "Рабочий парк: машины, которые сейчас работают у вас (свои и наёмные), и где каждая.",
      see: [
        "Цвет: зелёный — на заводе, синий — едет на объект, красный — на объекте, оранжевый — едет на завод.",
        "Водитель с телефоном, текущий и следующий рейс, сколько рейсов сегодня.",
      ],
      can: ["«Добавить машину в работу» и «Убрать из работы».", "Вручную отметить, где машина, — цветными кружками в строке.", "Открыть рейс — нажать на него."],
      tip: "Машину на рейс выбираете вы сами — в карточке заявки; система ничего не подбирает.",
    },
    manager: { about: "Где сейчас машины — чтобы ответить клиенту.", see: ["Цвет: на заводе, едет на объект, на объекте, едет на завод; текущий рейс каждой машины."], can: ["Открыть рейс — нажать на него."] },
    director: { about: "Рабочий парк и где каждая машина.", see: ["Сколько машин на заводе, в пути, на объектах; рейсы каждой за сегодня."], can: ["Открыть рейс — нажать на него."] },
  } },
  { page: "driver", title: "Мои рейсы", icon: "operator", text: {
    driver: {
      about: "Приложение водителя на телефоне: ваш рейс, очередь на погрузку, адрес объекта и кнопки статусов.",
      see: [
        "Сверху — ваш текущий рейс: марка и объём, заявка и номер рейса, время погрузки и место в очереди.",
        "Адрес объекта, «как проехать», телефон прораба, ожидаемое время прибытия.",
        "Ниже — следующие рейсы на сегодня и завтра.",
        "Полоса под заголовком — передаётся ли местоположение.",
      ],
      can: [
        "«Открыть в навигаторе» — маршрут до объекта в Яндекс Картах или Навигаторе; «Маршрут на завод» — обратно.",
        "Кнопка с именем прораба — позвонить ему; внизу — «Позвонить в диспетчерскую».",
        "Отметить статус одной большой кнопкой: «Я на заводе», «Я на объекте», «Начал разгрузку», «Разгрузился, еду на завод», «Вернулся на завод».",
      ],
      tip: "Отмечайте каждый шаг кнопкой — так диспетчер видит, где машина. Без связи нажатия сохраняются на телефоне и уйдут, когда связь появится.",
    },
  } },
  { page: "schedule", title: "График отгрузок", icon: "schedule", text: {
    dispatcher: {
      about: "Главный экран диспетчера: все РБУ доступных заводов на шкале суток и заявки дня.",
      see: [
        "Сверху — индикатор дня: заказано м³, загрузка РБУ в %, резерв м³, метка «Перегруз».",
        "Строки — РБУ, блоки — рейсы во время погрузки. Цвет: синий — запланирован или машина назначена, жёлтый — очередь или погрузка, оранжевый — в пути или на объекте, зелёный — разгрузка, возврат, завершён, красный — наложение.",
        "Список заявок дня с полосой «отгружено / осталось» и метками «срочная», «ждёт лабораторию».",
      ],
      can: [
        "«+ Заявка» — новая заявка с проверкой окна; галочка «Сразу подтвердить и поставить в график».",
        "Открыть заявку: «Подтвердить», «Изменить», «Перенести», «Отменить заявку», «Клиент остановил приём».",
        "В рейсах: «Назначить» машину и водителя, следующий статус рейса, «Отменить рейс», «На другую заявку», накладная; «Печать всех накладных» — ТТН всех отгруженных рейсов заявки сразу.",
        "Перетащить рейс на другое время или РБУ (до начала погрузки); при наложении — только с причиной.",
      ],
      tip: "Рейс попадает в очередь оператора, только когда заявка подтверждена и на рейс назначена машина.",
    },
    manager: {
      about: "График дня: что и когда грузится на каждой РБУ, и заявки дня.",
      see: ["Индикатор дня (заказано, загрузка, резерв, перегруз), рейсы по РБУ, заявки с прогрессом отгрузки."],
      can: [
        "«+ Заявка» — принять заявку: система сразу скажет, свободно ли, и предложит 3 ближайших варианта.",
        "Регулярная заявка — галочка в форме заявки.",
        "Открыть заявку: «Изменить», «Перенести» (в том числе все заявки объекта), «Отменить заявку», задачи.",
      ],
      tip: "Подтверждает заявку и назначает машины диспетчер — менеджер только принимает и ведёт клиента.",
    },
    director: {
      about: "График дня всех заводов — для просмотра.",
      see: ["Индикатор дня, рейсы по РБУ с цветом статуса, заявки дня с прогрессом."],
      can: ["Открыть любую заявку и посмотреть рейсы, машины, сроки и историю изменений."],
    },
    lab: {
      about: "График дня — чтобы видеть заявки с особыми марками.",
      see: ["Заявки с меткой «ждёт лабораторию» — их нельзя подтвердить и грузить, пока марку не подтвердит лаборатория."],
      can: ["Открыть заявку и нажать «Лаборатория: подтвердить марку» или «Отклонить»."],
    },
  } },
  { page: "week", title: "Неделя", icon: "week", text: {
    dispatcher: {
      about: "Загрузка заводов на 7 дней вперёд и регулярные заявки.",
      see: ["Таблица «дни × заводы»: м³ и загрузка в %; полоса краснеет выше 85%.", "Ниже — регулярные заявки: клиент, объект, марка, объём, время, дни, период."],
      can: ["Нажать на день — открыть график этого дня.", "Регулярную заявку — «Изменить» (применяется ко всем будущим дням) или «Остановить»."],
    },
    manager: {
      about: "Сколько стоит на каждый день недели — чтобы предложить клиенту свободный день.",
      see: ["Объём и загрузка каждого завода по дням; регулярные заявки."],
      can: ["Перейти в график дня.", "Изменить или остановить регулярную заявку."],
    },
    director: {
      about: "Загрузка заводов на неделю вперёд.",
      see: ["Объём и загрузка по дням и заводам; регулярные заявки."],
      can: ["Открыть график любого дня."],
    },
  } },
  { page: "shift", title: "Смена и задачи", icon: "shift", text: {
    dispatcher: {
      about: "Передача смены за 5–10 минут вместо часа пересказа.",
      see: [
        "Заметки предыдущей смены и кто её принял.",
        "Проблемные рейсы и предупреждения: машина не назначена, погрузка не начата, опоздание, заявка не успевает.",
        "Открытые задачи (просроченные отмечены). Новые, перенесённые, отменённые и изменённые заявки за 72 часа.",
        "Отгрузки без системы, которые нужно привязать к заявке.",
      ],
      can: ["«Принять смену», «Открыть смену», «Сдать смену» с заметками.", "Добавить задачу, отметить выполненной.", "Привязать отгрузку без системы к рейсу."],
      tip: "У каждого диспетчера свой логин: по нему видно в журнале, кто что сделал за смену.",
    },
    manager: {
      about: "Что произошло за последние трое суток и открытые задачи.",
      see: ["Проблемные рейсы, задачи, новые, перенесённые и отменённые заявки за 72 часа."],
      can: ["Отметить задачу выполненной, перейти к заявке."],
    },
    director: {
      about: "Сводка диспетчерской: проблемы, задачи, изменения за 72 часа.",
      see: ["То же, что видит диспетчер при приёме смены."],
      can: ["Перейти к любой заявке из списка."],
    },
  } },
  { page: "stock", title: "Склад сырья", icon: "stock", text: {
    lab: {
      about: "Сырьё на заводах: сколько есть, на сколько хватит, что привезли и куда ушло.",
      see: [
        "«Остатки»: материал, остаток, «ниже минимума», расход в сутки и на сколько дней хватит, сколько ждёт входного контроля.",
        "«Приход», «Движения» (в том числе списание по рецептуре на каждый замес), «Инвентаризации», «Отчёт за период».",
      ],
      can: ["«Приход», «Списание» (с причиной), «Инвентаризация» — вписать факт, расхождения проведутся сами.", "Отменить ошибочный приход."],
    },
    dispatcher: {
      about: "Хватит ли сырья на завтрашние заявки.",
      see: ["Остатки по заводам, «Хватит на … дн.», предупреждения о нехватке (они же — в колокольчике)."],
      can: ["Оформить приход, если машину с сырьём принимаете вы.", "Отменить ошибочный приход."],
    },
    manager: {
      about: "Остатки сырья — для понимания, сможет ли завод выполнить крупный заказ.",
      see: ["Остатки, расход в сутки и на сколько дней хватит; отчёт за период."],
      can: ["Только просмотр."],
    },
    director: {
      about: "Движение сырья: приход, расход по рецептурам, расхождения инвентаризаций.",
      see: ["Остатки по заводам; «Отчёт за период»: на начало, приход, расход, прочее, на конец и расход на 1 м³ по заводу."],
      can: ["Выгрузить отчёт в Excel."],
    },
    admin: {
      about: "Склад сырья всех заводов.",
      see: ["Остатки, приходы, движения, инвентаризации, отчёт."],
      can: ["Всё, что делают лаборатория и оператор.", "Список материалов и минимальные остатки — «Справочники» → «Сырьё (материалы)»."],
    },
  } },
  { page: "lab", title: "Лаборатория", icon: "lab", text: {
    lab: {
      about: "Входной контроль сырья, рецептуры с версиями, испытания образцов, паспорта качества.",
      see: [
        "Сверху: сколько партий ждёт контроля, каким испытаниям пора, сколько серий не соответствует классу, у каких марок нет рецептуры.",
        "«Входной контроль»: партии «на проверке»; «Рецептуры»: версии по маркам и где какая действует; «Испытания»; «Паспорта качества».",
      ],
      can: [
        "Партия: «Проверить» → показатели → «Годен» или «Не годен» (с причиной — тогда возврат поставщику).",
        "Рецептура: «Новая версия» → поправить нормы → «Сохранить черновик» → «Утвердить».",
        "«Отбор проб» → позже «Внести результаты»: вывод «соответствует / не соответствует» считается сам.",
      ],
      tip: "Утверждённую рецептуру не правят — создают новую версию с датой начала действия. Старые замесы остаются списанными по старой.",
    },
    director: {
      about: "Качество: входной контроль, рецептуры, испытания, паспорта.",
      see: ["Всё, что видит лаборатория, без права менять."],
      can: ["Открыть любой паспорт качества."],
    },
    admin: {
      about: "Раздел лаборатории.",
      see: ["Входной контроль, рецептуры, испытания, паспорта."],
      can: ["Всё, что делает лаборатория. Коэффициент требуемой прочности и пороги склада — «Администрирование» → «Настройки»."],
    },
  } },
  { page: "operator", title: "Экран оператора", icon: "operator", text: {
    operator: {
      about: "Рабочее место у РБУ: крупные кнопки под сенсорный монитор, только свой завод.",
      see: [
        "«Очередь»: текущая машина крупно — госномер, марка, объём, объект, комментарий оператору; ниже — следующие машины.",
        "«Сменное задание»: все рейсы смены и итог по маркам — для печати в операторскую.",
        "«Выпуск за смену»: факт по маркам против задания, возвраты, список замесов.",
        "«Сырьё»: остатки своего завода и движения за сегодня.",
      ],
      can: [
        "«Замес начат», «Погрузка завершена» с фактическим объёмом; печать накладной.",
        "«Возврат бетона» — возврат на завод или утилизация, при необходимости дополнительный рейс.",
        "«Отгрузка без системы (нет связи)» — записать отгрузку, если сеть упала.",
        "«Открыть смену», «Закрыть смену»; «Ночная тема» для ночных смен.",
      ],
      tip: "Очередь обновляется сама за секунды после изменения диспетчером; изменённый рейс подсвечивается.",
    },
  } },
  { page: "dashboard", title: "Дашборд", icon: "dashboard", text: {
    director: {
      about: "Главный экран руководителя: план и факт без звонков. Работает и с телефона.",
      see: [
        "«Отгружено сегодня» против плана дня, по заводам.",
        "«План месяца, %» — отгружено с 1-го числа против плана поставок месяца.",
        "«Темп и прогноз месяца» — сколько в среднем отгружаем в сутки, сколько выйдет к концу месяца при таком темпе и сколько нужно в сутки, чтобы выполнить план.",
        "Заявки в работе, проблемы («Требуют внимания»), загрузка каждой РБУ.",
        "Доля рейсов в срок сегодня и за месяц, среднее опоздание; «Отгрузки по часам» — план и факт.",
      ],
      can: ["Открыть проблемную заявку из списка «Требуют внимания»."],
    },
  } },
  { page: "plan", title: "План поставок", icon: "plan", text: {
    manager: {
      about: "Годовой план по клиентам и объектам с разбивкой по месяцам и фактом рядом.",
      see: ["В клетке — «принято в план», ниже «заявлено клиентом» и отгружено по факту. Строки «вне плана» и резерв на непредвиденные."],
      can: [
        "Нажать на месяц — изменить план; «Показать по дням» — завод работает без выходных, объём делится на все дни месяца.",
        "«Сдвинуть» — сдвинуть план объекта на N месяцев (стройка задержалась).",
        "«Добавить строку», «Импорт из Excel».",
      ],
      tip: "План/факт и дашборд считаются от «принято в план», а не от обещаний клиента.",
    },
    director: {
      about: "Годовой план поставок и выполнение по клиентам.",
      see: ["План, заявленное клиентом и факт по месяцам; итоги."],
      can: ["Править план, сдвигать объект, импортировать из Excel — как менеджер."],
    },
  } },
  { page: "reports", title: "Отчёты", icon: "report", text: {
    director: {
      about: "Отчёты за любой период с выгрузкой в Excel.",
      see: ["«Отгрузки», «Выполнение заявок», «Переносы и отмены», «Соблюдение сроков», «План/факт»; группировка по дням, заводам, клиентам, объектам, маркам."],
      can: ["Выбрать период и группировку, «Выгрузить в Excel»."],
    },
    manager: {
      about: "Отчёты по отгрузкам и клиентам.",
      see: ["Те же отчёты, что у руководителя."],
      can: ["Выбрать период и группировку, «Выгрузить в Excel»."],
    },
  } },
  { page: "catalogs", title: "Справочники", icon: "catalog", text: {
    manager: {
      about: "Клиенты, объекты, контакты и марки — то, на чём держатся расчёты.",
      see: ["Клиенты (ИНН, договор, приоритет), объекты (зона, интенсивность, как проехать, время в пути от заводов), контакты, марки."],
      can: ["«Добавить», изменить, «Импорт из Excel» (клиенты, объекты, марки, транспорт)."],
    },
    dispatcher: {
      about: "Транспорт, РБУ и их простои, объекты.",
      see: ["Транспорт: госномер, барабан, свой или наёмный, статус, водитель и телефон. Плановые простои РБУ."],
      can: ["Добавить машину, отправить в ремонт или выходной.", "Поставить плановый простой РБУ (ремонт, ТО) — в это время ничего не встанет."],
    },
    director: {
      about: "Все справочники системы.",
      see: ["Клиенты, объекты, марки, транспорт, заводы, РБУ, зоны доставки, причины задержек."],
      can: ["Просматривать и править."],
    },
    lab: {
      about: "Марки бетона и признак «нужна лаборатория».",
      see: ["Марки: класс, подвижность, F, W, наполнитель, зимняя, требует подтверждения лаборатории."],
      can: ["Отметить марки, которые нельзя отгружать без подтверждения лаборатории."],
    },
    admin: {
      about: "Все справочники, включая параметры РБУ, от которых зависит расписание.",
      see: ["РБУ: погрузка миксера, буфер между погрузками, стандартный рейс — эти числа напрямую задают время погрузок."],
      can: ["Добавлять и править любые справочники, загружать из Excel."],
    },
  } },
  { page: "admin", title: "Администрирование", icon: "admin", text: {
    admin: {
      about: "Пользователи, настройки расчёта и журнал действий.",
      see: ["«Пользователи» — роли, заводы, PIN, состояние.", "«Настройки» — допуск опоздания, пороги предупреждений, срочность, шаг поиска окна.", "«Журнал действий» — кто, когда, что изменил, было → стало, причина."],
      can: ["«Добавить пользователя», сменить пароль и PIN, закрыть вход.", "Изменить настройки.", "Найти в журнале изменения по разделу, сотруднику, периоду."],
      tip: "Смена пароля завершает все сессии человека.",
    },
  } },
];

type S = HelpScenario<R, Page>;
const D: R[] = ["dispatcher", "admin"];
const M: R[] = ["manager", "dispatcher", "admin"];

const DRIVER_SCENARIOS: HelpScenario<R, Page>[] = [
  { id: "drv-login", roles: ["driver"], page: "driver", title: "Как войти в приложение водителя", steps: [
    "Откройте на телефоне адрес системы (его даст диспетчер).",
    "Нажмите «Я водитель: войти по телефону».",
    "Введите свой номер телефона и код, который продиктовал диспетчер → «Войти».",
  ], note: "Телефон запомнит вход. Если код потерялся — диспетчер выдаст новый, старый перестанет работать." },
  { id: "drv-install", roles: ["driver"], page: "driver", title: "Как поставить значок на главный экран", steps: [
    "Android (Chrome): меню ⋮ → «Добавить на главный экран» или «Установить приложение».",
    "iPhone (Safari): «Поделиться» (квадрат со стрелкой) → «На экран „Домой“».",
  ], note: "Дальше открывайте приложение значком — как обычную программу." },
  { id: "drv-trip", roles: ["driver"], page: "driver", title: "Как пройти рейс от начала до конца", steps: [
    "Приехали на завод — «Я на заводе». Ждите погрузку: на экране место в очереди, время и госномер машины, за которой вы стоите.",
    "Погрузку и выезд отмечает оператор, накладную забираете у него.",
    "«Открыть в навигаторе» — маршрут до объекта. Приехали — «Я на объекте».",
    "Начали сливать — «Начал разгрузку»; закончили — «Разгрузился, еду на завод».",
    "Вернулись — «Вернулся на завод». На экране появится следующий рейс.",
    "Рейсов нет, а вы на заводе — нажмите «Я на заводе»: диспетчер увидит, что машина свободна и рядом.",
  ], note: "Если на заводе включили автоматические отметки по местоположению, часть шагов поставится сама — кнопки всё равно работают." },
  { id: "drv-gps", roles: ["driver"], page: "driver", title: "Как включить передачу местоположения (для диспетчера)", steps: [
    "При первом рейсе телефон спросит разрешение на местоположение — нажмите «Разрешить».",
    "Если отказали: настройки браузера → сайты → этот адрес → «Местоположение» → «Разрешить».",
    "Держите приложение открытым во время рейса: в свёрнутом браузере телефон может не передавать координаты.",
  ] },
  { id: "drv-offline", roles: ["driver"], page: "driver", title: "Что делать, если пропала связь", steps: [
    "Нажимайте кнопки как обычно — отметки сохранятся на телефоне (вверху будет «Нет связи»).",
    "Когда связь появится, всё уйдёт само, с тем временем, когда вы нажимали.",
  ] },
  { id: "drv-problem", roles: ["driver"], page: "driver", title: "Что делать, если рейс на экране не тот", steps: [
    "Страница обновляется сама каждые 20 секунд — подождите.",
    "Если рейса нет или он чужой — «Позвонить в диспетчерскую»: диспетчер переназначит машину.",
  ] },
  { id: "drivers", roles: ["dispatcher", "admin"], page: "catalogs", title: "Как завести водителя и выдать ему код", steps: [
    "«Справочники» → «Водители» → «Добавить»: ФИО, телефон, машина по умолчанию, перевозчик (для наёмных) → «Сохранить».",
    "Откройте водителя → «Выдать код». Продиктуйте водителю шестизначный код — он показывается один раз.",
    "Водитель входит на телефоне: «Я водитель: войти по телефону» → свой номер и код.",
  ], note: "Новый код отменяет старый и выходит со всех телефонов. Уволился — снимите «Работает»: войти больше не сможет." },
  { id: "fleet", roles: ["dispatcher", "manager", "director", "admin"], page: "fleet", title: "Как увидеть, где сейчас все машины", steps: [
    "Раздел «Машины»: все машины, которые работают у вас сейчас.",
    "Цвет: зелёный — на заводе, синий — едет на объект, красный — на объекте, оранжевый — едет на завод; серый — неизвестно.",
    "Кнопки сверху — показать только машины на заводе, на объекте и т. д.; у каждой — текущий и следующий рейс, водитель с телефоном.",
  ], note: "Где машина — по отметкам оператора, водителя и диспетчера. Без рейса водитель жмёт «Я на заводе» в приложении." },
  { id: "fleet-duty", roles: D, page: "fleet", title: "Как добавить машину в работу или убрать (уехала на другой завод)", steps: [
    "Раздел «Машины» → «Добавить машину в работу» → найдите по госномеру → «В работу».",
    "Уехала подработать на другой завод — в её строке «Убрать из работы». Вернулась — снова «Добавить машину в работу».",
  ], note: "При назначении на рейс показываются только машины в работе — список короткий, как привыкли." },
  { id: "fleet-place", roles: D, page: "fleet", title: "Как вручную отметить, где машина", steps: [
    "Раздел «Машины» → в строке машины цветные кружки справа: на заводе, едет на объект, на объекте, едет на завод.",
    "Нажмите нужный — например, водитель позвонил, что уже на объекте, а телефон не отметил.",
  ] },
  { id: "client-link", roles: M, page: "schedule", title: "Как дать клиенту ссылку «где моя машина»", steps: [
    "Откройте заявку → «Ссылка для клиента» → «Скопировать».",
    "Вставьте текст клиенту или прорабу в мессенджер или SMS.",
  ], note: "По ссылке без входа видно: статус заказа, госномера, время прибытия, телефоны водителя и диспетчерской. Цен и других заявок там нет. Ссылка работает 3 дня после даты заявки." },
  { id: "gps-card", roles: D, page: "schedule", title: "Как понять, где машина и когда приедет", steps: [
    "В карточке заявки, в рейсах: «ожид. ~14:55» — прогноз прибытия (выезд + время в пути до объекта).",
    "«GPS: 2 мин назад» — когда телефон водителя последний раз передавал координаты.",
    "Статусы ставят оператор, водитель кнопками и диспетчер. Если в настройках включены автоматические отметки по местоположению, часть ставится сама — в «Истории» это «Автоматически по геозоне».",
  ], note: "Карта с машинами — Этап 4." },
  { id: "late-return", roles: D, page: "schedule", title: "Что делать с предупреждением «не успевает к рейсу»", steps: [
    "Предупреждение появляется, когда машина ещё на объекте или в дороге и по прогнозу не вернётся к погрузке своего следующего рейса.",
    "Откройте заявку из предупреждения → у рейса «Сменить» → выберите свободную машину.",
  ] },
  { id: "idle", roles: ["dispatcher", "director", "manager", "admin"], page: "reports", title: "Как увидеть простой машин на объектах", steps: [
    "Сейчас: предупреждение «простаивает на объекте N мин сверх нормы» и метка «простой +N мин» в рейсах заявки.",
    "За период: «Отчёты» → «Простой на объектах»: по объектам и по рейсам; «Выгрузить в Excel».",
  ], note: "Норма — плановая разгрузка плюс допуск (15 минут, «Администрирование» → «Настройки»)." },
  { id: "coords", roles: ["admin", "dispatcher"], page: "catalogs", title: "Как включить автоматические отметки по местоположению", steps: [
    "По умолчанию выключено: у аэропорта местоположение телефона бывает неточным, статусы отмечают люди.",
    "Включить: «Администрирование» → «Настройки» → «Ставить статусы рейса по местоположению телефона» → «да».",
    "«Справочники» → «Заводы» и «Объекты»: заполните широту и долготу (в любой карте: правый клик по точке). Размер зон — там же в настройках.",
  ], note: "Без координат для этого завода или объекта отметки по местоположению не работают — только кнопки." },
];

const SCENARIOS: S[] = [
  // ------------------------------------------------------------ заявки
  { id: "new-order", roles: M, page: "schedule", title: "Как принять заявку от клиента", steps: [
    "«График отгрузок» → «+ Заявка».",
    "Клиент — начните вводить название, ИНН или телефон и выберите из списка; объект, марка и контакт подставятся по последней заявке.",
    "Проверьте объект, марку, укажите объём, дату и время первой машины, подачу, интенсивность приёма, канал (откуда пришла заявка).",
    "Справа «Проверка окна»: «свободно» — сохраняйте; «занято» — выберите вариант «Раньше», «Позже» или «Точно ко времени» на другой РБУ (согласуйте с клиентом).",
    "Диспетчер может отметить «Сразу подтвердить и поставить в график».",
    "«Сохранить» или Ctrl+Enter.",
  ], result: "Заявка с номером, рейсы стоят в графике. Если до даты меньше 2 дней — метка «срочная».", note: "Если похоже на дубль (тот же клиент, объект, дата, объём) — система предупредит и спросит, сохранять ли." },
  { id: "window", roles: ["manager", "dispatcher", "admin"], page: "schedule", title: "Как сразу ответить клиенту, есть ли окно", steps: [
    "«+ Заявка», выберите клиента и объект, введите объём, дату и время.",
    "Через секунду справа: «свободно» с РБУ, числом рейсов и интервалом — или ближайшие варианты.",
    "Если клиент не готов — «Отмена»: ничего не сохранится.",
  ] },
  { id: "recurring", roles: M, page: "schedule", title: "Как оформить регулярную заявку", steps: [
    "«+ Заявка», заполните клиента, объект, марку, объём, дату начала и время.",
    "Отметьте «Регулярная заявка: повторять по дням недели», выберите дни и «по» какую дату.",
    "«Сохранить регулярную».",
  ], result: "Система сама создаёт заявки на каждый отмеченный день (на 14 дней вперёд, дальше — по мере приближения). Список — на экране «Неделя».", note: "Если в какой-то день окна нет — система скажет, сколько дней не встало; их видно в графике." },
  { id: "recurring-edit", roles: M, page: "week", title: "Как изменить или остановить регулярную заявку", steps: [
    "«Неделя» → внизу «Регулярные заявки» → «Изменить».",
    "Поменяйте объём, время, дни или дату окончания → «Сохранить». Чтобы прекратить — «Остановить».",
  ], result: "Будущие заявки пересоздаются по шаблону; сегодняшняя и изменённые отдельно не трогаются." },
  { id: "edit", roles: M, page: "schedule", title: "Как изменить объём, марку или комментарий заявки", steps: [
    "Откройте заявку → «Изменить».",
    "Поменяйте объём, марку, контакт, интенсивность, приоритет или комментарии; укажите причину.",
    "Для заявки по регулярному шаблону выберите «Только эту заявку» или «Эту и все будущие по шаблону».",
    "«Сохранить». Если больший объём не помещается в прежнее окно — выберите один из предложенных вариантов времени.",
  ], result: "Рейсы пересчитаны; изменение видно во вкладке «История» заявки." },
  { id: "reschedule", roles: M, page: "schedule", title: "Как перенести заявку по просьбе прораба", steps: [
    "Откройте заявку → «Перенести».",
    "Новая дата и время, причина («бригада не готова») → «Перенести».",
    "Если окно занято — нажмите один из предложенных вариантов.",
  ], result: "Рейсы пересчитаны, оператор видит новую очередь за пару секунд. Перенос на другой день создаёт новую связанную заявку." },
  { id: "site-shift", roles: M, page: "schedule", title: "Как сдвинуть все заявки объекта (стройка задержалась)", steps: [
    "Откройте любую заявку объекта → «Перенести».",
    "Отметьте «Сдвиг стройки: перенести все заявки объекта».",
    "На сколько дней (минус — раньше) и причина → «Перенести все заявки объекта».",
  ], result: "Перенесены новые и подтверждённые заявки объекта с этой даты; если где-то нет окна — система назовёт эти заявки." },
  { id: "cancel", roles: M, page: "schedule", title: "Как отменить заявку или остаток", steps: [
    "Откройте заявку.",
    "Целиком — «Отменить заявку» и причина.",
    "Если часть уже отгружена и клиент остановил приём — «Клиент остановил приём»: остаток отменится, заявка станет «Частично выполнена».",
  ] },
  { id: "files", roles: M, page: "schedule", title: "Как приложить к заявке письмо или фото", steps: [
    "Откройте заявку → вкладка «Файлы» → «Приложить файл».",
    "Выберите файл: PDF, фото, Word, Excel, письмо (EML, MSG) — до 15 МБ.",
    "Открыть — нажмите на название; лишний — «Убрать».",
  ], result: "Файл виден всем, кто работает с заявкой; кто и когда приложил — в «Истории»." },
  { id: "task", roles: ["manager", "dispatcher", "admin", "lab", "director"], page: "schedule", title: "Как поставить задачу «перезвонить клиенту»", steps: [
    "Откройте заявку → вкладка «Задачи» → текст («перезвонить прорабу в 16:00») → «Добавить задачу».",
    "Или «Смена и задачи» → поле задачи → «Добавить» (диспетчер).",
  ], result: "Задача видна следующей смене, пока её не отметят выполненной." },
  // ------------------------------------------------------------ диспетчер
  { id: "confirm", roles: D, page: "schedule", title: "Как подтвердить заявку менеджера", steps: [
    "В списке заявок дня откройте заявку со статусом «Новая».",
    "Проверьте время и РБУ → «Подтвердить».",
  ], result: "Статус «Подтверждена»; рейсы попадут к оператору, когда назначены машины.", note: "Заявку с меткой «ждёт лабораторию» подтвердить нельзя, пока лаборант не подтвердит марку." },
  { id: "assign", roles: D, page: "schedule", title: "Как назначить машину на рейс", steps: [
    "Откройте заявку, вкладка «Рейсы» → у рейса «Назначить».",
    "В списке — машины, которые сейчас работают у вас, по госномеру; цветной кружок — где машина. Поиск по номеру, водителю, перевозчику. Нужна машина не из рабочего парка — «все машины».",
    "Пометки «занята: заявка №…, рейс …» (видно, каким рейсом и до какого времени, ссылка «открыть» ведёт в ту заявку) и «мал барабан» — такую выбрать нельзя. Кого ставить — решаете вы: система машину не подбирает.",
    "Водитель подставится из карточки машины; если сегодня сменщик — впишите его ФИО.",
    "«Назначить». Сменить — «Сменить», снять — «Снять машину» (до начала погрузки).",
  ], result: "Рейс «Машина назначена» и появляется в очереди оператора РБУ." },
  { id: "drag", roles: D, page: "schedule", title: "Как передвинуть рейс на графике", steps: [
    "Возьмите блок рейса мышью и перетащите на другое время или другую РБУ (до начала погрузки).",
    "Если место занято — система предупредит; сохранить с наложением можно только с причиной.",
  ] },
  { id: "statuses", roles: D, page: "schedule", title: "Как отмечать рейс: на объекте, разгрузка, возврат", steps: [
    "Откройте заявку → «Рейсы»: справа кнопка следующего статуса («На объекте», «Разгрузка», «Возврат», «Завершён»).",
    "Если машина приехала позже допуска — выберите причину задержки (виновная сторона подставится) и комментарий.",
  ], result: "Время каждого шага сохраняется; опоздание видно в отчёте «Соблюдение сроков».", note: "Погрузку и выезд отмечает оператор РБУ." },
  { id: "trip-cancel", roles: D, page: "schedule", title: "Как отменить рейс", steps: [
    "Заявка → «Рейсы» → «Отменить рейс» (до начала погрузки) → причина.",
  ], note: "Выехавший рейс не отменяют — оператор отмечает возврат бетона." },
  { id: "redirect", roles: D, page: "schedule", title: "Как отправить рейс на другую заявку (клиент сменил объект)", steps: [
    "Заявка → «Рейсы» → «На другую заявку».",
    "Выберите заявку этого дня с той же маркой, укажите причину → «Перенаправить».",
  ], result: "Рейс со своей машиной уходит к другой заявке; прогресс обеих заявок пересчитан." },
  { id: "handover", roles: ["dispatcher"], page: "shift", title: "Как принять и сдать смену", steps: [
    "Приход: «Смена и задачи» — прочитайте заметки, проблемные рейсы, задачи и изменения за 72 часа → «Принять смену».",
    "Если предыдущая смена принята — «Открыть смену».",
    "Уход: «Сдать смену» → заметки для следующей смены → «Сдать смену».",
  ], result: "Следующий диспетчер видит всё на экране — без пересказа и стикеров." },
  { id: "task-close", roles: D, page: "shift", title: "Как закрыть задачу смены", steps: [
    "Отметьте галочку у задачи — откроется окно «Закрыть задачу?».",
    "При желании впишите «Результат»: что сделали, с кем договорились → «Закрыть задачу».",
    "Случайный щелчок ничего не закроет: «Не закрывать» оставляет задачу открытой.",
  ], result: "Кто и когда закрыл задачу и с каким результатом — видно в «Истории смен»." },
  { id: "shift-history", roles: ["dispatcher", "admin", "director"], page: "shift", title: "Как посмотреть историю смен (старший диспетчер)", steps: [
    "«Смена и задачи» → вкладка «История смен» (видят администратор, руководитель и старший диспетчер).",
    "Выберите период и чьи смены: диспетчеры или операторы РБУ.",
    "В карточке смены: кто вёл и кто принял, заметки при сдаче, сколько заявок принято; «Задачи смены» — заведены, закрыты (с результатом), переданы следующей смене.",
  ], result: "Признак «Старший диспетчер» ставит администратор в карточке пользователя." },
  { id: "manual-link", roles: D, page: "shift", title: "Как привязать отгрузку без системы к заявке", steps: [
    "«Смена и задачи» → блок «Отгрузки без системы — привязать к заявке».",
    "У отгрузки — подходящие рейсы (та же марка и завод, рядом по времени; сначала с той же машиной). Нажмите нужный.",
  ], result: "Рейс «Выехал», фактический объём и номер накладной подставлены." },
  { id: "vehicle", roles: ["dispatcher", "admin"], page: "catalogs", title: "Как добавить машину или отправить её в ремонт", steps: [
    "«Справочники» → «Транспорт».",
    "Новая — «Добавить»: госномер, барабан, свой или наёмный, база, водитель и телефон.",
    "В ремонт или выходной — откройте машину, «Статус» → «Сохранить». Такая машина не предлагается при назначении.",
  ] },
  { id: "downtime", roles: ["dispatcher", "admin"], page: "catalogs", title: "Как закрыть РБУ на ремонт", steps: [
    "«Справочники» → «Плановые простои РБУ» → «Добавить»: РБУ, начало, окончание, причина.",
  ], result: "В это время на РБУ ничего не поставится; простой виден на графике." },
  // ------------------------------------------------------------ оператор
  { id: "op-shift", roles: ["operator"], page: "operator", title: "Как начать и закончить смену на РБУ", steps: [
    "В начале — «Открыть смену».",
    "В конце — «Закрыть смену» с комментарием.",
  ], result: "Выпуск за смену считается по этой смене." },
  { id: "op-load", roles: ["operator"], page: "operator", title: "Как отгрузить машину", steps: [
    "Вкладка «Очередь»: сверху текущая машина — госномер, марка, объём, объект, комментарий.",
    "Машина под загрузкой — «Замес начат».",
    "Загрузили — «Погрузка завершена», введите фактический объём; если отличается от плана — укажите причину. «Подтвердить».",
    "«Печать» — накладная для водителя.",
  ], result: "Рейс «Выехал», номер накладной выдан, следующая машина поднимается наверх." },
  { id: "op-return", roles: ["operator"], page: "operator", title: "Как отметить возврат бетона", steps: [
    "«Возврат бетона» → «Возврат на завод» или «Утилизация», объём, причина.",
    "Если клиенту не хватило — отметьте «Добавить дополнительный рейс в ту же заявку».",
  ], result: "Возврат виден в «Выпуске за смену»; дополнительный рейс система поставит сама." },
  { id: "op-manual", roles: ["operator"], page: "operator", title: "Что делать, если пропала связь", steps: [
    "Если экран показывает «Нет связи», отметки «Замес начат» и «Погрузка завершена» сохраняются и уйдут сами.",
    "Если машины нет в очереди — «Отгрузка без системы (нет связи)»: госномер, марка, объём.",
  ], result: "Диспетчер потом привяжет такую отгрузку к заявке." },
  { id: "op-task", roles: ["operator"], page: "operator", title: "Как распечатать сменное задание и выпуск за смену", steps: [
    "Вкладка «Сменное задание» → «Печать».",
    "Вкладка «Выпуск за смену» — факт по маркам против задания, «Погрузки по машинам»: начало и конец погрузки каждой машины.",
    "«Печать» в «Погрузках по машинам» — лист с временем погрузок и интервалом между машинами: для разговора с клиентом о темпе выпуска.",
  ] },
  { id: "op-shift", roles: ["operator"], page: "operator", title: "Как принять и сдать смену оператора", steps: [
    "Вкладка «Смена». Если предыдущий оператор сдал смену — прочитайте его заметки и нажмите «Принять смену».",
    "Если смены не было — «Открыть смену».",
    "В конце — «Сдать смену», впишите заметки для сменщика (неисправности, остатки, договорённости).",
  ], result: "Кто был на смене и что передал — видно в «Истории смен» у администратора и старшего диспетчера." },
  { id: "op-theme", roles: ["operator"], page: "operator", title: "Как включить ночную тему", steps: ["Вверху экрана — «Ночная тема» (обратно — «Светлая тема»)."] },
  { id: "op-stock", roles: ["operator"], page: "operator", title: "Как оформить приход сырья и инвентаризацию", steps: [
    "Вкладка «Сырьё» → «Приход сырья»: материал, количество, поставщик, накладная, госномер → «Оформить приход».",
    "Если у материала входной контроль — партия «на проверке», пока лаборатория не решит.",
    "«Инвентаризация» — впишите, сколько есть по факту (замер, взвешивание) → «Провести».",
    "Просыпали, промыли бункер — «Списание» с причиной.",
  ], result: "Списание по рецептуре идёт само — при «Погрузка завершена» по фактическому объёму." },
  // ------------------------------------------------------------ склад и лаборатория (Этап 3)
  { id: "stock-low", roles: ["lab", "dispatcher", "manager", "director", "admin"], page: "stock", title: "Как узнать, хватит ли сырья", steps: [
    "«Склад сырья» → «Остатки»: колонки «Состояние» и «Хватит на».",
    "Красное «нет на складе» и жёлтое «ниже минимума» приходят и в колокольчик.",
  ], note: "«Хватит на» — по среднему расходу за последние дни (настраивается)." },
  { id: "stock-report", roles: ["director", "manager", "admin", "lab"], page: "stock", title: "Как сверить расход сырья за период", steps: [
    "«Склад сырья» → «Отчёт за период» → даты и завод.",
    "Смотрите «Расход по рецептуре», «Прочее» (инвентаризации, списания, возвраты) и «На 1 м³».",
    "«Выгрузить в Excel» — для бухгалтерии.",
  ] },
  { id: "lab-control", roles: ["lab", "admin"], page: "lab", title: "Как провести входной контроль партии", steps: [
    "«Лаборатория» → «Входной контроль» → у партии «Проверить».",
    "Впишите показатели (можно добавить свои) и заключение.",
    "«Годен» — или «Не годен» с причиной.",
  ], result: "«Не годен» — партия уходит из остатка как возврат поставщику; всё сохраняется в журнале." },
  { id: "lab-recipe", roles: ["lab", "admin"], page: "lab", title: "Как поменять рецептуру марки", steps: [
    "«Лаборатория» → «Рецептуры» → у действующей версии «Новая версия».",
    "Поправьте нормы на 1 м³ (в килограммах, вода — в литрах), дату «Действует с» и комментарий → «Сохранить черновик».",
    "Проверьте и нажмите «Утвердить».",
  ], result: "С указанной даты замесы списывают сырьё по новой версии. Своя рецептура завода важнее общей." },
  { id: "lab-order-recipe", roles: ["lab", "admin"], page: "schedule", title: "Как задать рецептуру для конкретной заявки", steps: [
    "Откройте заявку → блок «Качество» → «Рецептура» → выберите утверждённую версию.",
  ], note: "Подтверждая особую марку, можно сразу указать рецептуру." },
  { id: "lab-test", roles: ["lab", "admin"], page: "lab", title: "Как внести испытание образцов", steps: [
    "«Лаборатория» → «Испытания» → «Отбор проб»: завод, марка, дата, возраст (7 или 28 сут), осадка конуса → «Сохранить».",
    "Когда подойдёт срок (в колонке «Испытать» — «пора»), «Внести результаты»: прочность каждого образца, МПа.",
  ], result: "Вывод считается сам: средняя не ниже класса × коэффициент, ни один образец не ниже класса." },
  { id: "passport", roles: ["lab", "dispatcher", "manager", "admin"], page: "schedule", title: "Как выдать паспорт качества", steps: [
    "Откройте заявку (после первой отгрузки) → блок «Качество» → «Выдать паспорт качества».",
    "Паспорт откроется для печати. Повторно — «Паспорт качества»; после догрузки — «Выдать заново».",
  ], result: "Все паспорта — «Лаборатория» → «Паспорта качества»." },
  // ------------------------------------------------------------ лаборатория
  { id: "lab", roles: ["lab", "admin", "director"], page: "schedule", title: "Как подтвердить особую марку", steps: [
    "В «Графике отгрузок» найдите заявку с меткой «ждёт лабораторию» и откройте её.",
    "«Лаборатория: подтвердить марку» или «Отклонить».",
  ], result: "После подтверждения заявку можно подтвердить и грузить; при отклонении — нет." },
  { id: "lab-grade", roles: ["lab", "admin"], page: "catalogs", title: "Как отметить марку, которой нужна лаборатория", steps: [
    "«Справочники» → «Марки бетона» → марка → «Требует подтверждения лаборатории» → «Сохранить».",
  ] },
  // ------------------------------------------------------------ менеджер: план, справочники, отчёты
  { id: "plan", roles: ["manager", "director", "admin"], page: "plan", title: "Как вести план поставок", steps: [
    "«План поставок» → нажмите на месяц у клиента.",
    "«Принято в план, м³» и «Заявлено клиентом, м³» → «Сохранить». «Показать по дням» — разбивка на все дни месяца (завод работает 24/7).",
    "Новый клиент или объект — «Добавить строку».",
  ], result: "Факт рядом с планом считается сам по отгрузкам." },
  { id: "plan-shift", roles: ["manager", "director", "admin"], page: "plan", title: "Как сдвинуть план объекта на месяц", steps: [
    "«План поставок» → у строки объекта «Сдвинуть» → на сколько месяцев → «Сдвинуть».",
  ], result: "Весь план объекта переехал одной операцией, в том числе через границу года." },
  { id: "plan-import", roles: ["manager", "director", "admin"], page: "plan", title: "Как загрузить годовой план из Excel", steps: [
    "«План поставок» → «Импорт из Excel» → файл со столбцами: клиент, месяц, объём (по желанию объект и «заявлено»).",
  ] },
  { id: "customer", roles: ["manager", "dispatcher", "admin"], page: "catalogs", title: "Как завести клиента и объект", steps: [
    "«Справочники» → «Клиенты» → «Добавить»: название, ИНН, телефоны, договор, приоритет.",
    "«Объекты» → «Добавить»: клиент, адрес, зона доставки, подача, интенсивность приёма, как проехать; время в пути от заводов — в карточке объекта.",
    "«Контакты» — прораб и снабженец с телефонами.",
    "Много записей — «Импорт из Excel».",
  ] },
  { id: "report", roles: ["director", "manager", "admin"], page: "reports", title: "Как выгрузить отчёт в Excel", steps: [
    "«Отчёты» → вид отчёта: отгрузки, выполнение заявок, переносы и отмены, соблюдение сроков, план/факт.",
    "Период и группировка (по дням, заводам, клиентам, объектам, маркам).",
    "В «План/факт» по каждому периоду — план и факт, справа — итог, отклонение и процент (зелёный — план выполнен, красный — ниже 90%).",
    "«Выгрузить в Excel».",
  ] },
  { id: "dashboard", roles: ["director", "admin"], page: "dashboard", title: "Как за минуту понять, как идёт день", steps: [
    "«Дашборд»: отгружено сегодня против плана по заводам, выполнение месяца, доля рейсов в срок.",
    "«Требуют внимания» — проблемные заявки; нажмите, чтобы открыть.",
    "«Загрузка РБУ» — где установка простаивает («Нет заявок до …»).",
  ] },
  // ------------------------------------------------------------ администратор
  { id: "user", roles: ["admin"], page: "admin", title: "Как завести сотрудника", steps: [
    "«Администрирование» → «Пользователи» → «Добавить пользователя».",
    "ФИО, логин, телефон, роль, пароль (не короче 6 символов), PIN 4–6 цифр для входа за общим ПК, заводы.",
    "Оператору — один завод: он увидит только свою РБУ.",
  ], note: "Каждому диспетчеру — свой логин, а не общий аккаунт: иначе журнал и передача смены теряют смысл." },
  { id: "block", roles: ["admin"], page: "admin", title: "Как сменить пароль или закрыть доступ", steps: [
    "«Пользователи» → сотрудник → новый пароль или PIN → «Сохранить» (все его сессии завершатся).",
    "Уволился — снимите «Активен»: войти не сможет, история сохранится.",
  ] },
  { id: "settings", roles: ["admin"], page: "admin", title: "Как поменять допуск опоздания и пороги предупреждений", steps: [
    "«Администрирование» → «Настройки».",
    "Допуск опоздания, по чему считать опоздание (прибытие или выезд), за сколько минут предупреждать, срочность заявки, шаг поиска окна → «Сохранить».",
  ] },
  { id: "journal", roles: ["admin"], page: "admin", title: "Как узнать, кто и что изменил", steps: [
    "«Администрирование» → «Журнал действий».",
    "Фильтры: раздел, сотрудник, период. Действие, затронувшее много записей (перенос заявки), — одна строка; «подробнее» раскрывает.",
  ], note: "История одной заявки — вкладка «История» в карточке заявки." },
  { id: "unit", roles: ["admin"], page: "catalogs", title: "Как настроить РБУ", steps: [
    "«Справочники» → «РБУ» → установка: производительность, погрузка миксера (мин), буфер между погрузками (мин), стандартный рейс (м³), активна.",
  ], note: "Эти числа напрямую задают расписание: проверьте их по факту с оператором." },
  ...DRIVER_SCENARIOS,
];

const ALL: R[] = ["manager", "dispatcher", "operator", "director", "admin", "lab"];
const WITH_DRIVER: R[] = [...ALL, "driver"];
const DESKR: R[] = ["manager", "dispatcher", "director", "admin", "lab"];
const TERMS: HelpTerm<R>[] = [
  { term: "Завод", roles: ALL, text: "Площадка с одной или несколькими установками. Сейчас: Завод 1 (две РБУ) и Завод 2 (одна)." },
  { term: "РБУ", roles: ALL, text: "Установка, где готовят и грузят бетон. Грузит одну машину за раз: время погрузки плюс буфер." },
  { term: "Заявка", roles: DESKR, text: "Заказ клиента: марка, объём, объект, время первой машины. У каждой — номер." },
  { term: "Рейс", roles: ALL, text: "Одна поездка одного миксера. Заявка на 27 м³ — три рейса по 9 м³." },
  { term: "Окно", roles: DESKR, text: "Свободное время на РБУ, когда можно погрузить все рейсы заявки подряд." },
  { term: "Интенсивность приёма", roles: DESKR, text: "Сколько кубов в час успевает принять объект (насос 30 м³/ч). От неё — интервал между машинами." },
  { term: "Интервал", roles: DESKR, text: "Через сколько минут приходит следующая машина: большее из «рейс ÷ интенсивность» и «погрузка + буфер»." },
  { term: "Время в пути", roles: DESKR, text: "От завода до объекта: из карточки объекта, иначе из зоны доставки, иначе 30 минут." },
  { term: "Срочная", roles: ["manager", "dispatcher", "admin"], text: "Заявка подана меньше чем за 2 дня до даты (настраивается)." },
  { term: "Регулярная заявка", roles: ["manager", "dispatcher", "director", "admin"], text: "Шаблон «по дням недели с … по …», по которому заявки создаются сами." },
  { term: "Наложение", roles: ["dispatcher", "admin"], text: "Две погрузки на одной РБУ одновременно. Запрещено, кроме случая с указанной причиной; на графике — красным." },
  { term: "Допуск опоздания", roles: ["dispatcher", "director", "admin"], text: "До скольки минут опоздание считается «в срок» (15). Считается от прибытия на объект." },
  { term: "Накладная", roles: ALL, text: "Документ на рейс; номер выдаётся, когда оператор нажал «Погрузка завершена»." },
  { term: "Замес (партия)", roles: ["operator", "director", "admin"], text: "Что реально выпустила РБУ на рейс: марка, объём план и факт, время." },
  { term: "Сменное задание", roles: ["operator", "dispatcher", "admin"], text: "Все рейсы смены РБУ по времени с итогом по маркам — для операторской." },
  { term: "Возврат бетона", roles: ["operator", "dispatcher", "director", "admin"], text: "Остаток вернули на завод или утилизировали; с объёмом и причиной." },
  { term: "Особая марка", roles: ["lab", "manager", "dispatcher", "admin"], text: "Марка с признаком «требует подтверждения лаборатории»: без подтверждения не подтверждается и не грузится." },
  { term: "План поставок", roles: ["manager", "director", "admin"], text: "Сколько каждый клиент возьмёт по месяцам. «Заявлено клиентом» — обещание, «принято в план» — на что рассчитывает завод." },
  { term: "Резерв", roles: ["manager", "director", "admin"], text: "Объём на непредвиденные заявки в плане года; в план месяца на дашборде не входит." },
  { term: "Передача смены", roles: ["dispatcher", "director", "admin"], text: "Сдать с заметками, принять с подтверждением; изменения за 72 часа видны на экране." },
  { term: "Водитель", roles: ["dispatcher", "manager", "director", "admin"], text: "Свой или наёмный; входит в приложение на телефоне по номеру и коду от диспетчера. За машиной — водитель по умолчанию." },
  { term: "Рецептура", roles: ["lab", "operator", "director", "admin", "dispatcher"], text: "Сколько каждого материала идёт на 1 м³ марки — в килограммах, вода в литрах (склад пересчитывает в тонны и кубометры сам). Версии: утверждённую не правят, создают новую с датой начала." },
  { term: "Входной контроль", roles: ["lab", "operator", "dispatcher", "admin"], text: "Проверка лабораторией привезённой партии сырья: «годен» или «не годен» (возврат поставщику)." },
  { term: "Требуемая прочность", roles: ["lab", "director", "admin"], text: "Класс бетона × коэффициент (1,28 по ГОСТ 18105). Серия образцов соответствует, если средняя не ниже требуемой и ни один не ниже класса." },
  { term: "Паспорт качества", roles: ["lab", "dispatcher", "manager", "director", "admin"], text: "Документ клиенту на заявку: марка и характеристики, объём по накладным, состав, результаты испытаний. Номер — по порядку." },
  { term: "Геозона", roles: ["dispatcher", "director", "admin"], text: "Круг вокруг завода (300 м) или объекта (200 м). Если включено в настройках — въехал или выехал, статус рейса ставится сам." },
  { term: "Рабочий парк", roles: ["dispatcher", "manager", "director", "admin"], text: "Машины, которые сейчас работают у вас (свои и наёмные). Из них выбирают на рейс; уехала на другой завод — «Убрать из работы»." },
  { term: "Место в очереди", roles: ["driver", "dispatcher"], text: "Сколько машин до вас на погрузку на вашей РБУ, считая вас. 1 — вы следующий. Ниже — «Вы за машиной …»: госномер машины прямо перед вами." },
  { term: "Прогноз прибытия", roles: WITH_DRIVER, text: "Выезд с завода плюс время в пути до объекта; до выезда — по плану, но не раньше «сейчас + путь»." },
  { term: "Простой на объекте", roles: ["dispatcher", "director", "manager", "admin"], text: "Время на объекте сверх плановой разгрузки и допуска (15 минут)." },
  { term: "Ссылка для клиента", roles: ["manager", "dispatcher", "admin"], text: "Страница «где машина» без входа; диспетчер копирует её из заявки и отправляет сам." },
  { term: "Рейс", roles: ["driver"], text: "Одна поездка: погрузка на заводе, дорога, разгрузка на объекте, возврат." },
  { term: "Накладная", roles: ["driver"], text: "Документ на рейс; печатает и отдаёт оператор после погрузки." },
  { term: "Предупреждение", roles: ["dispatcher", "manager", "director", "admin"], text: "Машина не назначена за 30 минут, погрузка не начата через 10 минут, опоздание, заявка не успевает до конца окна клиента." },
];

const CONTENT: HelpContent<R, Page> = { app: "beton", pages: PAGES, scenarios: SCENARIOS, terms: TERMS, primary: "admin" };

/** Администратору доступно всё, что диспетчеру, менеджеру и руководителю, — поэтому и их сценарии. */
function rolesOf(role: Role): R[] {
  if (role === "admin") return ["admin", "dispatcher", "manager", "director"];
  return [role];
}

const PATH: Record<Page, string> = {
  schedule: "/schedule", week: "/week", fleet: "/fleet", stock: "/stock", lab: "/lab", shift: "/shift", operator: "/operator", driver: "/driver", dashboard: "/dashboard",
  plan: "/plan", reports: "/reports", catalogs: "/catalogs", admin: "/admin",
};

export function HelpButton() {
  const { me } = useCtx();
  const r = useRoute();
  const here = ((r.parts[0] as Page) in PATH ? r.parts[0] : "schedule") as Page;
  return (
    <SharedHelp content={CONTENT} userId={me.id} roles={rolesOf(me.role)} roleLabel={ROLES[me.role] || me.role} here={here}
      pathOf={(p) => PATH[p]} searchHint="Поиск: заявка, машина, смена, накладная…" />
  );
}
